import { apiFetch } from '../api-client';

export type AssetStatus = 'in_use' | 'depleted' | 'maintenance' | 'disposed';
export type DepreciationMethod = 'straight' | 'declining' | 'units';

export interface Asset {
  id: string;
  code: string;
  name: string;
  categoryCode: string;
  category?: { code: string; name: string; account: string; years: number };
  department: string | null;
  userName: string | null;
  assignedEmployeeId: string | null;
  purchasedAt: string;
  cost: number;
  depreciatedPct: number;
  remaining: number;
  status: AssetStatus;
  notes: string | null;

  // Mua sắm
  serialNumber: string | null;
  supplier: string | null;
  invoiceNumber: string | null;
  warrantyUntil: string | null;

  // Cấu hình KH (nullable = fallback theo category)
  depreciationMethod: DepreciationMethod | null;
  depreciationYears: number | null;
  depreciationStartDate: string | null;
  costAccount: string | null;
  depreciationAccount: string | null;

  // Gán cho
  location: string | null;
  assignmentHistory?: AssetAssignment[];
  images?: AssetImage[];
}

export interface AssetImage {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface AssetAssignment {
  id: string;
  employeeId: string | null;
  employeeName: string | null;
  department: string | null;
  location: string | null;
  assignedAt: string;
  unassignedAt: string | null;
  assignedBy: string | null;
}

export interface AssetCategory {
  code: string;
  name: string;
  account: string;
  years: number;
  method: string;
  assetCount?: number;
}

export interface AssetsSummary {
  count: number;
  totalCost: number;
  totalRemaining: number;
  depletedCount: number;
  byStatus: Partial<Record<AssetStatus, number>>;
}

export interface CreateAssetInput {
  code: string;
  name: string;
  categoryCode: string;
  department?: string;
  userName?: string;
  assignedEmployeeId?: string;
  purchasedAt: string;
  cost: number;
  depreciatedPct?: number;
  status?: AssetStatus;
  notes?: string;

  serialNumber?: string;
  supplier?: string;
  invoiceNumber?: string;
  warrantyUntil?: string;

  depreciationMethod?: DepreciationMethod;
  depreciationYears?: number;
  depreciationStartDate?: string;
  costAccount?: string;
  depreciationAccount?: string;

  location?: string;
  imageAttachmentIds?: string[];
}

export type UpdateAssetInput = Partial<CreateAssetInput>;

export const assetsApi = {
  list: () => apiFetch<{ items: Asset[] }>('/api/assets'),
  summary: () => apiFetch<AssetsSummary>('/api/assets/summary'),
  findOne: (id: string) => apiFetch<Asset>(`/api/assets/${id}`),
  create: (input: CreateAssetInput) =>
    apiFetch<Asset>('/api/assets', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: UpdateAssetInput) =>
    apiFetch<Asset>(`/api/assets/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  remove: (id: string) => apiFetch<{ ok: true }>(`/api/assets/${id}`, { method: 'DELETE' }),

  listCategories: () => apiFetch<{ items: AssetCategory[] }>('/api/asset-categories'),
  createCategory: (input: AssetCategory) =>
    apiFetch<AssetCategory>('/api/asset-categories', { method: 'POST', body: JSON.stringify(input) }),
  updateCategory: (code: string, input: Partial<Omit<AssetCategory, 'code' | 'assetCount'>>) =>
    apiFetch<AssetCategory>(`/api/asset-categories/${code}`, { method: 'PATCH', body: JSON.stringify(input) }),
  removeCategory: (code: string) =>
    apiFetch<{ ok: true }>(`/api/asset-categories/${code}`, { method: 'DELETE' }),
};
